import React from "react";
import ReactGA from "react-ga4";
import request from "../../../utilities/requests";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../contexts/AppContext";
import ConfirmRedirectDialog from "./ConfirmRedirectDialog";
import PoolCards from "./PoolCards";
import RegistrationDialog from "./RegistrationDialog";
import { toast } from "../../../utilities/SnackBarUtils";

import "../events.css";

const AllEvents = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { events, setEvents } = React.useContext(AppContext);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [eventUrl, setEventUrl] = React.useState(null);
  const [registrationOpen, setRegistrationOpen] = React.useState(false);
  const [eventId, setEventId] = React.useState(null);
  const [ticketingOpen, setTicketingOpen] = React.useState(false);

  React.useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/events", title: "AllEvents" });
  }, []);

  React.useEffect(() => {
    if (!loaded) {
      getAllEvents();
      setLoaded(true);
      window.scrollTo(0, 0);
    }
  });

  const getAllEvents = async () => {
    const result = await request("events");
    if (result.success) {
      setEvents(result.events);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEventUrl(null);
  };

  const handleRegister = async (event) => {
    const result = await request(
      `ticketing/tickets/channel/?eventId=${event._id}`
    );
    if (result.success) {
      if (result.channel === "tnfm") {
        navigate(`/tickets/${event._id}`)
        // setEventId(event._id);
        // setTicketingOpen(true);
      } else {
        window.open(result.url, "_blank", "noreferrer");
      }
    } else {
      toast.error(result.error);
    }
  };

  const handleRegistrationClose = () => {
    setRegistrationOpen(false);
    setEventId(null);
    getAllEvents();
  };

  const handleOpenLink = () => {
    window.open(eventUrl, "_blank", "noreferrer");
    setOpen(false);
    setEventUrl(null);
    ReactGA.event({
      category: "events",
      action: "goto_eventbrite",
    });
  };

  const formatDate = (date) => {
    let d = new Date(date);
    let dateString = d.toDateString();
    let hours = d.getHours();
    let minutes = d.getMinutes();

    let timeString;
    if (hours > 12 && minutes === 0) {
      timeString = `${hours - 12} PM`;
    } else if (hours > 12 && minutes > 0) {
      timeString = `${hours - 12}:${minutes} PM`;
    } else if (hours <= 12 && minutes === 0) {
      timeString = `${hours} AM`;
    } else if (hours <= 12 && minutes > 0) {
      timeString = `${hours}:${minutes} AM`;
    }

    return `${dateString} @ ${timeString}`;
  };

  const getProximity = (date) => {
    if (!date) return null;

    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round(
      (startOfDay(new Date(date)) - startOfDay(new Date())) / 86400000,
    );

    if (days < 0) return null;
    if (days === 0) return { tone: "live", label: t("home.when.today", "Today") };
    if (days === 1)
      return { tone: "soon", label: t("home.when.tomorrow", "Tomorrow") };
    if (days <= 6)
      return { tone: "upcoming", label: t("home.when.thisweek", "This week") };

    return null;
  };

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      spacing={3}
      style={{ marginBottom: 50, padding: "20px 20px 200px 20px" }}
    >
      <PoolCards />
      <Grid item xs={12}>
        <Typography
          component="h2"
          className="ev-section"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "text.secondary",
            paddingBottom: "6px",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Upcoming events
          {events.length ? (
            <span style={{ opacity: 0.6 }}>{`  ·  ${events.length}`}</span>
          ) : null}
        </Typography>
      </Grid>
      {events.map((event) => {
        const when = getProximity(event.date);

        return (
          <Grid key={event._id} item xs={12}>
            <Card
              className="ev-card"
              style={{ "--ev-accent": theme.palette.primary.main }}
            >
              <CardMedia
                className="ev-media"
                image={event.imageUrl}
                title={event.name}
              >
                {when ? (
                  <span className={`ev-pill ev-pill--${when.tone}`}>
                    {when.label}
                  </span>
                ) : null}
              </CardMedia>
              <CardContent
                className={`ev-body${when ? "" : " ev-body--nopill"}`}
              >
                <Typography className="ev-eyebrow">
                  {formatDate(event.date)}
                </Typography>
                <Typography className="ev-title" component="h3">
                  {event.name}
                </Typography>
                <Typography className="ev-desc">
                  {event.description}
                </Typography>
              </CardContent>
              <CardActions className="ev-actions">
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  onClick={() => handleRegister(event)}
                >
                  {t("home.register.button")}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
      <ConfirmRedirectDialog
        open={open}
        close={handleClose}
        confirm={handleOpenLink}
      />
      <RegistrationDialog
        open={registrationOpen}
        close={() => handleRegistrationClose()}
        eventId={eventId}
      />
  
    </Grid>
  );
};

export default AllEvents;
